/**
 * The exact-decimal primitives the import pipeline's AMOUNT semantics are built
 * on — the ONE tuning point for financial arithmetic across every package.
 *
 * These lived in `packages/backend/src/platform/shared-utils/decimal.utils.ts`
 * until the in-browser template-editor preview became a third consumer of
 * `field-transform-semantics.ts` and could not import from the backend. Only
 * the three symbols that module needs moved here; the backend file re-exports
 * them, so its many other callers are unchanged and there is still exactly ONE
 * `Decimal.clone(...)` in the codebase.
 *
 * Why exact decimal and not float: crypto amounts carry more than the 15
 * significant digits a JS number holds (satoshis, wei), and `Number('1.234,56')`
 * is `NaN` — which the float paths then wrote into an amount as the string
 * 'NaN'. A number that is wrong is unrecoverable.
 */

import { Decimal } from 'decimal.js'

/**
 * Canonical Decimal constructor for financial amount arithmetic.
 *
 * Cloned (does NOT mutate the global `Decimal` config) with precision raised
 * from the default 20 to 50 significant digits and ROUND_HALF_UP (matching
 * PostgreSQL `numeric` rounding): exchange APIs and on-chain sources return
 * 18+ fractional digit amounts (satoshis, wei) that `parseFloat`/`Number`
 * silently corrupt, and a wei-scale integer divided by 10^18 needs 21+
 * significant digits.
 *
 * If the amount paths were ever tuned differently, identical amounts would
 * format differently → dedup-hash mismatch → duplicate transactions. That is
 * why this is ONE clone shared by every consumer rather than one per module.
 */
export const FinancialAmountDecimal = Decimal.clone({
  precision: 50,
  rounding: Decimal.ROUND_HALF_UP,
})

/**
 * Leading numeric prefix accepted by `parseFloat`: optional sign, digits with
 * optional fraction (or bare fraction), optional exponent. Used to mirror
 * `parseFloat`'s lenient prefix semantics ("334.850W" → 334.850) exactly, so
 * the Decimal path parses precisely WHEN AND WHAT the float path parses.
 */
const PARSE_FLOAT_PREFIX_PATTERN = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?/

/**
 * Parse a string with `parseFloat` semantics (leading numeric prefix, trailing
 * garbage ignored) into an exact Decimal. Returns `null` exactly when
 * `parseFloat` would return `NaN`. Note: `parseFloat('Infinity')` parses but is
 * useless as an amount — it is rejected here (`null`).
 */
export function parseDecimalWithParseFloatSemantics(raw: string): Decimal | null {
  const match = PARSE_FLOAT_PREFIX_PATTERN.exec(raw.trim())
  if (!match) {
    return null
  }
  return new FinancialAmountDecimal(match[0])
}

/**
 * Format a Decimal as a plain (never exponential) decimal string at FULL
 * precision with trailing fraction zeros trimmed — `0.133` not `0.13300000`,
 * `123.456789012345678901` kept intact. Same trailing-zero-trim convention as
 * the shared `formatCsvAmount`, minus its 8-decimal rounding. Negative zero is
 * canonicalized to `'0'`.
 */
export function formatDecimalAmountTrimmed(amount: Decimal): string {
  // `toFixed()` without arguments renders the full exact value in plain
  // notation (no '1e-9'), unlike `toString()`.
  const fixed = amount.toFixed()
  const trimmed = fixed.includes('.') ? fixed.replace(/\.?0+$/, '') : fixed
  // `0.000` trims to '0'; a negative that trims to '-0' (or was '-0') is zero.
  return trimmed === '-0' || trimmed === '' ? '0' : trimmed
}
